/**
 * @file App.jsx
 * @description Root application component. Configures React Router routes,
 * authentication context, SEO provider, and scroll-to-top behavior.
 * Defines all public, protected, and admin routes for the application.
 */

import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './Context/Auth';
import { SiteSettingsProvider } from './Context/SiteSettingsContext';
import ErrorBoundary from './Components/ErrorBoundary';
import Home from "./pages/Home";
import Tours from "./pages/Tours";
import About from "./pages/AboutUs";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import Wishlist from "./pages/Wishlist";
import Profile from "./pages/Profile";
import Blogs from "./pages/Blogs";
import {
  AdminLayout,
  Dashboard,
  Tours as ToursAdmin,
  Users as UsersAdmin,
  Bookings as BookingsAdmin,
  Testimonials as TestimonialsAdmin,
  Settings,
  ProtectedRoute
} from './Components';
import UserManagement from './pages/admin/UserManagement';
import Outbound from './pages/Outbound';
import CountryGuide from './pages/CountryGuide';
import CountryGuideManager from './pages/admin/CountryGuideManager';
import BlogDetail from './pages/BlogDetail';
import BlogManager from './pages/admin/BlogManager';
import BlogEditor from './pages/admin/BlogEditor';
import BookTour from './pages/BookTour';
import MyBookings from './pages/MyBookings';
import TourDetail from './pages/TourDetail';
import SingleTour from './pages/SingleTour';
import QueryManager from './Components/Admin/QueryManager';
import SiteContent from './Components/Admin/SiteContent';
import Search from './pages/Search';
import Hotels from './pages/Hotels';

// Component to handle scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Hidden admin shortcut: Ctrl+Alt+N → /admin (only for admins)
const AdminShortcut = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.altKey && (e.key.toLowerCase() === 'n' || e.code === 'KeyN')) {
        e.preventDefault();
        e.stopPropagation();
        // Check auth context first, then localStorage as fallback
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const isAdmin = user?.isAdmin || user?.role === 'admin' ||
                        storedUser?.isAdmin === true || storedUser?.role === 'admin';
        console.log('isAdmin:', isAdmin);
        if (isAdmin) {
          navigate('/admin');
        }
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [user, navigate]);

  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <SiteSettingsProvider>
          <AuthProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <AdminShortcut />
              <ToastContainer position="top-right" autoClose={3000} />
              <Routes>
                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="tours" element={<ToursAdmin />} />
                  <Route path="users" element={<UsersAdmin />} />
                  <Route path="user-management" element={<UserManagement />} />
                  <Route path="bookings" element={<BookingsAdmin />} />
                  <Route path="tours/:id/book" element={<BookTour />} />
                  <Route path="my-bookings" element={<MyBookings />} />
                  <Route path="testimonials" element={<TestimonialsAdmin />} />
                  <Route path="blog" element={<BlogManager />} />
                  <Route path="blog/new" element={<BlogEditor />} />
                  <Route path="blog/edit/:id" element={<BlogEditor />} />
                  <Route path="queries" element={<QueryManager />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="site-content" element={<SiteContent />} />
                </Route>

                {/* Protected Routes */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wishlist"
                  element={
                    <ProtectedRoute>
                      <Wishlist />
                    </ProtectedRoute>
                  }
                />

                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/tours" element={<Tours />} />
                <Route path="/tours/:id" element={<SingleTour />} />
                <Route path="/tours/:id/book" element={<BookTour />} />
                <Route path="/my-bookings" element={<MyBookings />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:id" element={<BlogDetail />} />
                <Route path="/search" element={<Search />} />
                <Route path="/hotels" element={<Hotels />} />
                <Route path="/outbound" element={<Outbound />} />
                <Route path="/travel-guide/:countryCode" element={<CountryGuide />} />
                <Route path="/admin/country-guides" element={<CountryGuideManager />} />
              </Routes>
            </Router>
          </AuthProvider>
        </SiteSettingsProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;